import { useCallback, useEffect, useRef, useState } from "react";
import {
  IMAGE_UPLOAD,
  ONE_MIB,
  type Image,
  type SupportedImageFormat,
} from "@photography-booking/shared";
import {
  deleteImage,
  getHeroRenditionKey,
  listLibraryImages,
  reorderImages,
  requestImageUpload,
  selectHeroImage,
  setImageInclusion,
  uploadToPresignedUrl,
} from "../api/admin-client.js";
import { assetUrl } from "../api/client.js";
import { AdminPanel, useAdminStatus } from "./AdminPanel.js";

interface PortfolioAdminProps {
  token: string;
}

/** True when the MIME type is in the shared upload allowlist (Req 4.2). */
function isSupportedFormat(type: string): type is SupportedImageFormat {
  return (IMAGE_UPLOAD.supportedFormats as readonly string[]).includes(type);
}

/**
 * Portfolio management view (Req 4.1, 4.4, 4.5).
 *
 * Lists the current images and supports: uploading a new image through the
 * presigned-PUT flow (`POST /admin/gallery` → PUT to object storage), deleting
 * an image (`DELETE /admin/gallery/{id}`), and reordering with move up/down
 * controls that persist the new order (`PUT /admin/gallery/order`). Upload
 * input is validated against the shared format allowlist and size limit before
 * any request is made, so the client mirrors the server's acceptance rule.
 */
export function PortfolioAdmin({ token }: PortfolioAdminProps): JSX.Element {
  const [images, setImages] = useState<Image[]>([]);
  const [heroKey, setHeroKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Drag-and-drop reorder state (desktop/mouse). Live reorder shifts items
  // under the pointer; the order is saved when the drag ends.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Draft values for the editable position number, keyed by imageId.
  const [posEdits, setPosEdits] = useState<Record<string, string>>({});
  // Snapshot of the order at drag start, for rollback if the save fails.
  const dragSnapshot = useRef<Image[] | null>(null);
  const { status, reportError, reportSuccess, clearStatus } = useAdminStatus();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Files chosen but not yet uploaded. The confirm modal previews each and
  // collects an optional comment + photo credit per image before uploading.
  const [pending, setPending] = useState<
    { file: File; url: string; caption: string; photoCredit: string }[] | null
  >(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [result, hero] = await Promise.all([
      listLibraryImages(token),
      getHeroRenditionKey(token),
    ]);
    setLoading(false);
    if (result.ok) {
      setImages([...result.data].sort((a, b) => a.displayOrder - b.displayOrder));
    } else {
      reportError(result.message);
    }
    if (hero.ok) {
      setHeroKey(hero.data);
    }
  }, [token, reportError]);

  /** Sets the chosen library image as the homepage hero (`PUT /admin/hero`). */
  const chooseHero = async (image: Image): Promise<void> => {
    clearStatus();
    setBusy(true);
    const result = await selectHeroImage(token, image.imageId);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setHeroKey(image.renditionKey ?? null);
    reportSuccess("Homepage hero updated.");
  };

  const toggleInclusion = async (
    imageId: string,
    included: boolean,
  ): Promise<void> => {
    clearStatus();
    setBusy(true);
    const result = await setImageInclusion(token, imageId, included);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setImages((current) =>
      current.map((image) =>
        image.imageId === imageId
          ? { ...image, includedInGallery: included }
          : image,
      ),
    );
    reportSuccess(included ? "Added to the gallery." : "Hidden from the gallery.");
  };

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Uploads a single file; returns an error string on failure, or null on success. */
  const uploadOne = async (
    file: File,
    caption?: string,
    photoCredit?: string,
  ): Promise<string | null> => {
    if (!isSupportedFormat(file.type)) {
      return `${file.name}: unsupported format`;
    }
    if (file.size > IMAGE_UPLOAD.maxSizeBytes) {
      const maxMib = Math.floor(IMAGE_UPLOAD.maxSizeBytes / ONE_MIB);
      return `${file.name}: larger than ${maxMib} MiB`;
    }
    const presigned = await requestImageUpload(token, {
      contentType: file.type,
      sizeBytes: file.size,
      fileName: file.name,
      caption: caption?.trim() || undefined,
      photoCredit: photoCredit?.trim() || undefined,
    });
    if (!presigned.ok) {
      return `${file.name}: ${presigned.message}`;
    }
    const uploaded = await uploadToPresignedUrl(presigned.data.uploadUrl, file);
    if (!uploaded.ok) {
      return `${file.name}: ${uploaded.message}`;
    }
    return null;
  };

  /**
   * Uploads every selected file (mobile and desktop both allow multi-select).
   * Each file is validated and uploaded independently so one bad file doesn't
   * abort the rest; a summary reports how many succeeded and lists any failures.
   */
  const handleUpload = async (
    entries: { file: File; caption?: string; photoCredit?: string }[],
  ): Promise<void> => {
    clearStatus();
    if (entries.length === 0) {
      return;
    }
    setBusy(true);
    let succeeded = 0;
    const errors: string[] = [];
    for (const entry of entries) {
      const error = await uploadOne(entry.file, entry.caption, entry.photoCredit);
      if (error) {
        errors.push(error);
      } else {
        succeeded += 1;
      }
    }
    setBusy(false);

    if (succeeded > 0) {
      await refresh();
    }
    if (errors.length === 0) {
      reportSuccess(
        `${succeeded} image${succeeded === 1 ? "" : "s"} uploaded. ` +
          `They will appear once processing completes.`,
      );
    } else if (succeeded > 0) {
      reportError(
        `${succeeded} uploaded, ${errors.length} failed — ${errors.join("; ")}`,
      );
    } else {
      reportError(`Upload failed — ${errors.join("; ")}`);
    }
  };

  /** Opens the confirm modal for the chosen files with local preview URLs. */
  const choosePending = (files: File[]): void => {
    if (files.length === 0) {
      return;
    }
    setPending(
      files.map((file) => ({
        file,
        url: URL.createObjectURL(file),
        caption: "",
        photoCredit: "",
      })),
    );
  };

  /** Updates one pending entry's caption or photo credit. */
  const updatePending = (
    index: number,
    key: "caption" | "photoCredit",
    value: string,
  ): void => {
    setPending((current) =>
      current
        ? current.map((entry, i) =>
            i === index ? { ...entry, [key]: value } : entry,
          )
        : current,
    );
  };

  /** Closes the modal and releases the preview object URLs. */
  const closePending = (): void => {
    setPending((current) => {
      current?.forEach((entry) => URL.revokeObjectURL(entry.url));
      return null;
    });
  };

  /** Confirms the modal: uploads each pending file with its entered details. */
  const confirmPendingUpload = async (): Promise<void> => {
    if (!pending) {
      return;
    }
    const entries = pending.map((entry) => ({
      file: entry.file,
      caption: entry.caption,
      photoCredit: entry.photoCredit,
    }));
    closePending();
    await handleUpload(entries);
  };

  const handleDelete = async (imageId: string): Promise<void> => {
    clearStatus();
    setBusy(true);
    const result = await deleteImage(token, imageId);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setImages((current) => current.filter((image) => image.imageId !== imageId));
    reportSuccess("Image deleted.");
  };

  const persistOrder = async (next: Image[]): Promise<void> => {
    clearStatus();
    const previous = images;
    setImages(next);
    setBusy(true);
    const result = await reorderImages(
      token,
      next.map((image) => image.imageId),
    );
    setBusy(false);
    if (!result.ok) {
      setImages(previous);
      reportError(result.message);
      return;
    }
    reportSuccess("Order saved.");
  };

  /**
   * Moves the image at `fromIndex` to a 1-based position. A position past the
   * end lands the image last; below 1 lands it first.
   */
  const moveToPosition = (fromIndex: number, position1Based: number): void => {
    if (!Number.isFinite(position1Based)) {
      return;
    }
    const target = Math.min(
      Math.max(Math.trunc(position1Based) - 1, 0),
      images.length - 1,
    );
    if (target === fromIndex) {
      return;
    }
    const next = [...images];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(target, 0, moved);
    void persistOrder(next);
  };

  /** Commits the edited position number for an image (on blur / Enter). */
  const commitPosition = (imageId: string, index: number): void => {
    const raw = posEdits[imageId];
    setPosEdits((current) => {
      const next = { ...current };
      delete next[imageId];
      return next;
    });
    if (raw === undefined || raw.trim() === "") {
      return;
    }
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      moveToPosition(index, parsed);
    }
  };

  /** Drag start: remember the current order so a failed save can roll back. */
  const onDragStartItem = (index: number): void => {
    setDragIndex(index);
    dragSnapshot.current = images;
  };

  /** Live reorder: shift the dragged item under the pointer as it moves. */
  const onDragOverItem = (index: number): void => {
    if (dragIndex === null || dragIndex === index) {
      return;
    }
    setImages((current) => {
      const next = [...current];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };

  /** Drag end: persist the new order (rolling back on failure). */
  const onDragEndItem = async (): Promise<void> => {
    const snapshot = dragSnapshot.current;
    dragSnapshot.current = null;
    setDragIndex(null);
    if (!snapshot) {
      return;
    }
    const changed =
      snapshot.length !== images.length ||
      snapshot.some((img, i) => img.imageId !== images[i]?.imageId);
    if (!changed) {
      return;
    }
    clearStatus();
    setBusy(true);
    const result = await reorderImages(
      token,
      images.map((image) => image.imageId),
    );
    setBusy(false);
    if (!result.ok) {
      setImages(snapshot);
      reportError(result.message);
    } else {
      reportSuccess("Order saved.");
    }
  };

  return (
    <AdminPanel
      title="Portfolio"
      description="Upload photos to your library. Toggle 'In gallery' to choose which appear on the public site, or 'Use as hero' to set the homepage background. Reorder by dragging a photo, or type a new position number. Photos stay in your library even when hidden."
      status={status}
    >
      <div className="admin-uploader">
        <input
          ref={fileInputRef}
          className="admin-uploader__input"
          type="file"
          multiple
          accept={IMAGE_UPLOAD.supportedFormats.join(",")}
          disabled={busy}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = "";
            choosePending(files);
          }}
        />
        <span className="admin-uploader__hint">
          Select one or more · {IMAGE_UPLOAD.supportedFormats.join(", ")} · up to{" "}
          {Math.floor(IMAGE_UPLOAD.maxSizeBytes / ONE_MIB)} MiB each. You can add
          an optional comment and photo credit in the next step.
        </span>
      </div>

      {loading ? (
        <p className="admin-empty">Loading images…</p>
      ) : images.length === 0 ? (
        <p className="admin-empty">No images yet. Upload one to get started.</p>
      ) : (
        <ul className="admin-list">
          {images.map((image, index) => (
            <li
              key={image.imageId}
              className={`admin-photo${
                dragIndex === index ? " admin-photo--dragging" : ""
              }`}
              draggable={!busy}
              onDragStart={(event) => {
                onDragStartItem(index);
                event.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                onDragOverItem(index);
              }}
              onDragEnd={() => void onDragEndItem()}
            >
              <span
                className="admin-drag-handle admin-photo__handle"
                aria-hidden="true"
                title="Drag to reorder"
              >
                ⠿
              </span>
              <img
                className="admin-thumb admin-photo__thumb"
                src={assetUrl(image.renditionKey ?? image.originalKey)}
                alt=""
                loading="lazy"
              />
              <div className="admin-photo__body">
                <label className="admin-photo__pos">
                  <span className="admin-field__label">Position</span>
                  <input
                    className="admin-field__input admin-photo__pos-input"
                    type="number"
                    min={1}
                    inputMode="numeric"
                    aria-label={`Position of image ${index + 1}`}
                    value={posEdits[image.imageId] ?? String(index + 1)}
                    disabled={busy}
                    onChange={(event) =>
                      setPosEdits((current) => ({
                        ...current,
                        [image.imageId]: event.target.value,
                      }))
                    }
                    onBlur={() => commitPosition(image.imageId, index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.currentTarget.blur();
                      }
                    }}
                  />
                </label>
                <div className="admin-photo__actions">
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={image.includedInGallery !== false}
                      disabled={busy}
                      onChange={(event) =>
                        void toggleInclusion(image.imageId, event.target.checked)
                      }
                    />
                    <span>In gallery</span>
                  </label>
                  {(() => {
                    const isHero =
                      heroKey !== null && image.renditionKey === heroKey;
                    return (
                      <button
                        type="button"
                        className={
                          isHero ? "admin-button" : "admin-button admin-button--primary"
                        }
                        disabled={busy || isHero}
                        onClick={() => void chooseHero(image)}
                        title="Use this photo as the homepage hero background"
                      >
                        {isHero ? "Current hero ✓" : "Use as hero"}
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    className="admin-button admin-button--danger"
                    disabled={busy}
                    onClick={() => void handleDelete(image.imageId)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pending ? (
        <div
          className="admin-modal__overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Add photo details"
        >
          <div className="admin-modal admin-modal--wide">
            <p className="admin-modal__title">
              Add details
              {pending.length > 1 ? ` (${pending.length} photos)` : ""}
            </p>
            <p className="admin-modal__body">
              Comment and “Photo by” are optional — they show under the photo in
              the gallery. Leave them blank to skip.
            </p>
            <ul className="admin-upload-list">
              {pending.map((entry, index) => (
                <li key={index} className="admin-upload-item">
                  <img className="admin-thumb" src={entry.url} alt="" />
                  <div className="admin-upload-item__fields">
                    <p className="admin-upload-item__name">{entry.file.name}</p>
                    <label className="admin-field">
                      <span className="admin-field__label">
                        Comment (optional)
                      </span>
                      <input
                        className="admin-field__input"
                        type="text"
                        maxLength={280}
                        placeholder="A caption shown under the photo"
                        value={entry.caption}
                        onChange={(e) =>
                          updatePending(index, "caption", e.target.value)
                        }
                      />
                    </label>
                    <label className="admin-field">
                      <span className="admin-field__label">
                        Photo by (optional)
                      </span>
                      <input
                        className="admin-field__input"
                        type="text"
                        maxLength={100}
                        placeholder="Photographer name / handle"
                        value={entry.photoCredit}
                        onChange={(e) =>
                          updatePending(index, "photoCredit", e.target.value)
                        }
                      />
                    </label>
                  </div>
                </li>
              ))}
            </ul>
            <div className="admin-list__actions">
              <button
                type="button"
                className="admin-button admin-button--primary"
                disabled={busy}
                onClick={() => void confirmPendingUpload()}
              >
                {busy
                  ? "Uploading…"
                  : `Upload ${pending.length} photo${
                      pending.length === 1 ? "" : "s"
                    }`}
              </button>
              <button
                type="button"
                className="admin-button"
                disabled={busy}
                onClick={closePending}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPanel>
  );
}
